import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageCircle, SearchCheck, Power, Eye, TrendingUp,
  Check, ArrowRight, Clock3, Sparkles, Lightbulb,
} from "lucide-react";
import { Reveal } from "@/components/od/Reveal";
import { MaskedLine } from "@/components/od/Hero";
import { useSimplify } from "@/components/od/simplify-context";
import { TRIAL_COPY } from "@/data/trialCopy";

const SITE = "https://oceandata.ca";

const STAGE_ICONS = {
  "stage-show": MessageCircle,
  "stage-find": SearchCheck,
  "stage-activate": Power,
  "stage-see": Eye,
  "stage-measure": TrendingUp,
};

function Stage({ stage, index, open, onToggle, labels }) {
  const Icon = STAGE_ICONS[stage.id];
  return (
    <div
      data-testid={stage.id}
      className={`rounded-2xl border transition-colors duration-300 overflow-hidden ${
        open
          ? stage.peak
            ? "border-brand-teal bg-brand-navy text-white"
            : "border-brand-teal bg-white"
          : "border-slate-200 bg-white hover:border-brand-teal/60"
      }`}
    >
      <button
        data-testid={`${stage.id}-toggle`}
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-center gap-4 sm:gap-6 px-5 sm:px-7 py-5 sm:py-6 text-left"
      >
        <span
          className={`font-display font-bold text-2xl sm:text-3xl w-10 shrink-0 ${
            open ? (stage.peak ? "text-brand-gold" : "text-brand-teal") : "text-slate-300"
          }`}
        >
          {String(index + 1).padStart(2, "0")}
        </span>
        <span
          className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
            open
              ? stage.peak
                ? "bg-brand-gold/25 text-brand-gold"
                : "bg-brand-tealsoft text-brand-teal"
              : "bg-slate-100 text-slate-400"
          }`}
        >
          <Icon size={17} />
        </span>
        <span className="flex-1 min-w-0">
          <span className={`block font-display font-semibold text-lg sm:text-xl leading-snug ${open ? (stage.peak ? "text-white" : "text-brand-navy") : "text-brand-navy"}`}>
            {stage.title}
          </span>
          <span className={`block text-xs mt-1 ${open ? (stage.peak ? "text-slate-300" : "text-slate-500") : "text-slate-400"}`}>
            {stage.when}
          </span>
        </span>
        <span className={`font-mono text-[10px] uppercase tracking-[0.14em] shrink-0 hidden sm:block ${open ? "text-brand-teal" : "text-slate-300"}`}>
          {open ? labels.openLabel : labels.tapLabel}
        </span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className={`px-5 sm:px-7 pb-7 pt-1 border-t ${stage.peak ? "border-white/10" : "border-slate-100"}`}>
              <p className={`mt-5 font-display font-medium text-base sm:text-lg leading-snug mb-5 ${stage.peak ? "text-brand-tealsoft" : "text-brand-navy"}`}>
                “{stage.ask}”
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                <div className={`rounded-xl p-4 ${stage.peak ? "bg-white/5" : "bg-[#F8FAFC]"}`}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.16em] font-semibold mb-2 text-slate-400">
                    {labels.youSide}
                  </p>
                  <p className={`text-sm leading-relaxed ${stage.peak ? "text-slate-200" : "text-slate-600"}`}>{stage.youDo}</p>
                </div>
                <div className={`rounded-xl p-4 ${stage.peak ? "bg-white/5" : "bg-brand-tealsoft/60"}`}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-brand-teal font-semibold mb-2">
                    {labels.ourSide}
                  </p>
                  <p className={`text-sm leading-relaxed ${stage.peak ? "text-slate-200" : "text-slate-600"}`}>{stage.weDo}</p>
                </div>
              </div>
              <p className={`flex items-start gap-2.5 text-xs sm:text-sm italic ${stage.peak ? "text-slate-300" : "text-slate-500"}`}>
                <Lightbulb size={14} className="text-brand-teal mt-0.5 shrink-0" />
                {stage.sticky}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function TrialPage({ lang = "en" }) {
  const { setOpen } = useSimplify();
  const t = TRIAL_COPY[lang] || TRIAL_COPY.en;
  const [openIdx, setOpenIdx] = useState(0);
  const [visited, setVisited] = useState(new Set([0]));

  useEffect(() => {
    document.title = t.docTitle;
    document.documentElement.lang = lang;
    const links = [];
    Object.entries(t.hreflangs).forEach(([hreflang, path]) => {
      const el = document.createElement("link");
      el.rel = "alternate";
      el.hreflang = hreflang;
      el.href = `${SITE}${path}`;
      el.setAttribute("data-hreflang-injected", "true");
      document.head.appendChild(el);
      links.push(el);
    });
    return () => {
      document.documentElement.lang = "en";
      links.forEach((el) => el.remove());
    };
  }, [t, lang]);

  const toggle = (i) => {
    setOpenIdx(i);
    setVisited((v) => new Set(v).add(i));
  };

  const labels = { openLabel: t.openLabel, tapLabel: t.tapLabel, youSide: t.youSide, ourSide: t.ourSide };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: t.schemaName,
    inLanguage: t.schemaLang,
    description: t.schemaDesc,
    totalTime: "P30D",
    step: t.stages.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.title,
      text: `${s.youDo} ${s.weDo}`,
    })),
  };

  return (
    <main className="pt-16 sm:pt-20 bg-[#F8FAFC]" lang={lang}>
      <section className="py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-6">
            {t.eyebrow}
          </p>
          <h1
            data-testid="trial-headline"
            className="font-display font-bold tracking-tight leading-[1.04] text-brand-navy text-4xl sm:text-5xl lg:text-6xl"
          >
            <MaskedLine delay={0.15} testId="trial-headline-line-1">{t.h1a}</MaskedLine>
            <MaskedLine delay={0.3} testId="trial-headline-line-2">
              <span className="text-brand-teal">{t.h1b}</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            {t.heroSub}
          </motion.p>

          {/* progress rail */}
          <div data-testid="trial-progress-rail" className="flex items-center gap-2.5 mt-12 mb-10">
            {t.stages.map((s, i) => (
              <button
                key={s.id}
                data-testid={`trial-rail-${i}`}
                onClick={() => toggle(i)}
                aria-label={`${t.stepLabel(i)}: ${s.title}`}
                className="group flex-1 flex flex-col items-start gap-2"
              >
                <span
                  className={`h-1.5 w-full rounded-full transition-colors duration-300 ${
                    i === openIdx ? "bg-brand-teal" : visited.has(i) ? "bg-brand-navy/60 group-hover:bg-brand-navy" : "bg-slate-200 group-hover:bg-slate-300"
                  }`}
                />
                <span className={`font-mono text-[10px] uppercase tracking-[0.12em] ${i === openIdx ? "text-brand-teal font-semibold" : "text-slate-400"}`}>
                  {visited.has(i) && i !== openIdx ? (
                    <span className="inline-flex items-center gap-1"><Check size={11} strokeWidth={3} /> {i + 1}</span>
                  ) : (
                    t.stepLabel(i)
                  )}
                </span>
              </button>
            ))}
          </div>

          <div className="space-y-4">
            {t.stages.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.05}>
                <Stage stage={s} index={i} open={openIdx === i} onToggle={() => toggle(i)} labels={labels} />
              </Reveal>
            ))}
          </div>

          {/* anticipatory note */}
          <Reveal delay={0.1}>
            <div className="mt-12 rounded-2xl bg-white border border-slate-200 p-7 sm:p-8 flex items-start gap-4">
              <span className="w-11 h-11 rounded-full bg-brand-tealsoft text-brand-teal flex items-center justify-center shrink-0">
                <Sparkles size={18} />
              </span>
              <div>
                <p className="font-display font-semibold text-lg text-brand-navy mb-1.5">
                  {t.noteTitle}
                </p>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                  {t.noteBody}{" "}
                  <Link to="/demos" className="font-semibold text-brand-teal hover:text-brand-navy transition-colors">
                    {t.noteLink}
                  </Link>
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* closing CTA */}
      <section className="bg-brand-navy text-white py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 text-center">
          <Reveal>
            <p className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-brand-teal font-semibold mb-5">
              <Clock3 size={14} /> {t.closeEyebrow}
            </p>
            <h2 className="font-display font-semibold tracking-tight leading-[1.12] text-2xl sm:text-3xl lg:text-4xl max-w-2xl mx-auto mb-5">
              {t.closeTitle}
            </h2>
            <p className="text-base text-slate-300 leading-relaxed max-w-xl mx-auto mb-9">
              {t.closeBody}{" "}
              <Link to={t.closePricingTo} className="font-semibold text-brand-teal hover:text-white transition-colors">
                {t.closePricingLink}
              </Link>
            </p>
            <button
              data-testid="trial-bottom-cta"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-3 rounded-full bg-brand-teal text-white text-base font-semibold px-10 py-4 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
            >
              {t.closeCta}
              <ArrowRight size={18} />
            </button>
          </Reveal>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }} />
    </main>
  );
}
